import { Link } from "react-router-dom";
import { ArrowUpRight, Factory, Trash2, Zap, Wheat, Mountain, Plane, Building2 } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const SECTORS = [
  {
    icon: Factory,
    title: "Steel & Smelting",
    desc: "Scrap-based steel plants, ferro alloys, fume extraction, bag filters and waste heat recovery power for the metals value chain.",
    tags: ["EAF steel plants", "Ferro alloys", "WHR power", "Air pollution control"],
    testid: "industry-steel",
  },
  {
    icon: Trash2,
    title: "Municipal & Solid Waste",
    desc: "Solid municipal waste converted to biogas, RNG, CBG and CNG — with organic fertilizer and biogenic carbon as co-products.",
    tags: ["Biogas / RNG", "CBG & CNG", "Organic fertilizer", "Tyre to TDF"],
    testid: "industry-waste",
  },
  {
    icon: Zap,
    title: "Power & Utilities",
    desc: "Thermal, solar CSP, syngas, biogas and biomass power plants backed by thermal, compressed-air, liquid-air and CO2 battery storage.",
    tags: ["Solar CSP", "Biomass power", "Long-duration storage", "Waste heat recovery"],
    testid: "industry-power",
  },
  {
    icon: Wheat,
    title: "Agriculture & Forestry",
    desc: "Agri and forest waste to gas, bio pellets, biochar and bio coal — plus liquid and solid organic fertilizer returning carbon to soil.",
    tags: ["Agri waste to CBG", "Bio pellets", "Biochar", "Organic fertilizer"],
    testid: "industry-agri",
  },
  {
    icon: Building2,
    title: "Construction & Green Building",
    desc: "Green cement, bricks, blocks and engineered cast profiles & sections — circular building materials that lock recovered resources into the built environment.",
    tags: ["Green cement", "Bricks & blocks", "Cast profiles & sections", "Low-carbon construction"],
    testid: "industry-building",
  },
  {
    icon: Mountain,
    title: "Mining & Metallurgy",
    desc: "End-of-life mines restored as solar and wind power plants, with bioremediation returning mined land to productive agriculture.",
    tags: ["Mine restoration", "Solar / wind repowering", "Bioremediation", "Air separation gases"],
    testid: "industry-mining",
  },
  {
    icon: Plane,
    title: "Aviation & Biofuels",
    desc: "Biomass pathways to sustainable aviation fuel, methanol and ethanol — plus micro algae platforms for next-generation feedstocks.",
    tags: ["SAF", "Green methanol", "Ethanol", "Micro algae"],
    testid: "industry-aviation",
  },
];

const Industries = () => (
  <div data-testid="industries-page" className="pt-28 sm:pt-36 pb-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>Industries we serve</Eyebrow>
        <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
          Where waste streams become <span className="text-gradient">revenue streams</span>
        </h1>
        <p className="mt-6 max-w-2xl text-gray-300 text-base sm:text-lg leading-relaxed">
          Seven industrial sectors, one circular approach — engineered plants that convert
          liabilities into energy, materials and restored land.
        </p>
      </Reveal>

      <div className="mt-14 grid md:grid-cols-2 gap-6">
        {SECTORS.map((s, i) => (
          <Reveal key={s.title} delay={(i % 2) * 0.08}>
            <article
              data-testid={s.testid}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-carbon hover:border-lime/50 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(132,204,22,0.12)] transition-all duration-500"
            >
              <div className="relative h-24 border-b border-white/10 bg-gradient-to-r from-lime/10 via-carbon to-carbon flex items-end p-7 overflow-hidden">
                <s.icon className="text-lime" size={28} />
                <s.icon className="absolute -right-3 -top-4 text-white/[0.05] group-hover:text-lime/10 group-hover:rotate-6 transition-all duration-700" size={100} />
              </div>
              <div className="p-7">
                <h2 className="font-display font-bold text-2xl text-white group-hover:text-lime transition-colors">
                  {s.title}
                </h2>
                <p className="mt-3 text-sm text-gray-400 leading-relaxed">{s.desc}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span key={t} className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-400 border border-white/10 rounded-full px-3 py-1">
                      {t}
                    </span>
                  ))}
                </div>
                <Link
                  to="/contact"
                  data-testid={`${s.testid}-enquire`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-lime hover:gap-2.5 transition-all duration-300"
                >
                  Discuss your sector <ArrowUpRight size={15} />
                </Link>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </div>
);

export default Industries;
